// UserProfileJsonLd.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import serialize from 'serialize-javascript';
import { withTranslation } from 'react-i18next';

import { ProfileListType } from '../resource/profileConstants.js';
import getProfileTdk from '../resource/getProfileTdk.js';
import {
  getPerson,
  getFlixVideoObject,
  getFlixPreviewVideoObject,
  getInteractionCounter,
  getStoryVideoObject,
  getStoryPreviewVideoObject,
} from '../resource/SchemaObject.js';

export class UserProfileJsonLd extends React.PureComponent {
  render() {
    const {
      type,
      userId,
      username,
      biography,
      flixDataList,
      storyDataList,
      t,
    } = this.props;
    const { displayTitle: pageName } = getProfileTdk({
      username,
      biography,
      listType: type,
      t,
    });
    const { displayTitle: storyPageName } = getProfileTdk({
      username,
      biography,
      listType: ProfileListType.STORY,
      t,
    });
    const { displayTitle: flixPageName } = getProfileTdk({
      username,
      biography,
      listType: ProfileListType.FLIX,
      t,
    });
    const { displayTitle: imagePageName } = getProfileTdk({
      username,
      biography,
      listType: ProfileListType.IMAGE,
      t,
    });
    return (
      <Helmet>
        <script type="application/ld+json" helmetkey="jsonld">
          {serialize(
            {
              '@context': 'https://schema.org',
              '@graph': [
                getPerson({
                  id: userId,
                  username,
                  biography,
                  hasOfferCatalog: [
                    {
                      '@id': `https://swag.live/user/${userId}#stories`,
                      url: `https://swag.live/user/${userId}`,
                    },
                    {
                      '@id': `https://swag.live/user/${userId}/flix#flixes`,
                      url: `https://swag.live/user/${userId}/flix`,
                    },
                    {
                      '@id': `https://swag.live/user/${userId}/image#images`,
                      url: `https://swag.live/user/${userId}/image`,
                    },
                  ],
                }),
                {
                  '@type': 'ProfilePage',
                  '@id': `https://swag.live/user/${userId}/${type}#webpage`,
                  url: `https://swag.live/user/${userId}/${type}`,
                  name: pageName,
                  breadcrumb: {
                    '@id': `https://swag.live/user/${userId}/${type}#breadcrumb`,
                  },
                  mainEntity: {
                    '@id': `https://swag.live/user/${userId}#person`,
                  },
                  significantLinks: [
                    `https://swag.live/user/${userId}`,
                    `https://swag.live/user/${userId}/flix`,
                    `https://swag.live/user/${userId}/image`,
                  ],
                },
                {
                  '@type': 'BreadcrumbList',
                  '@id': `https://swag.live/user/${userId}/${type}#breadcrumb`,
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: t('seo-home-title'),
                      item: 'https://swag.live',
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: storyPageName,
                      item: `https://swag.live/user/${userId}`,
                    },
                    ProfileListType.FLIX === type && {
                      '@type': 'ListItem',
                      position: 3,
                      name: flixPageName,
                      item: `https://swag.live/user/${userId}/flix`,
                    },
                    ProfileListType.IMAGE === type && {
                      '@type': 'ListItem',
                      position: 3,
                      name: imagePageName,
                      item: `https://swag.live/user/${userId}/image`,
                    },
                  ].filter(a => a),
                },
                {
                  '@type': 'OfferCatalog',
                  '@id': `https://swag.live/user/${userId}#stories`,
                  url: `https://swag.live/user/${userId}`,
                  itemListElement: storyDataList.map((storyData, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `https://swag.live/story/${storyData.id}#preview`,
                    },
                  })),
                },
                {
                  '@type': 'OfferCatalog',
                  '@id': `https://swag.live/user/${userId}/flix#flixes`,
                  url: `https://swag.live/user/${userId}/flix`,
                  itemListElement: flixDataList.map((flixData, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `https://swag.live/flix/${flixData.id}#preview`,
                    },
                  })),
                },
                {
                  '@type': 'OfferCatalog',
                  '@id': `https://swag.live/user/${userId}/image#images`,
                  url: `https://swag.live/user/${userId}/image`,
                  itemListElement: flixDataList.map((flixData, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `https://swag.live/post/${flixData.id}#preview`,
                    },
                  })),
                },
                ...storyDataList.map(messageData => {
                  return getStoryPreviewVideoObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    username,
                    caption: messageData.caption,
                    durationSecs: messageData.media?.duration,
                    postedAtUnix: messageData.postedAtUnix,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                  });
                }),
                ...storyDataList.map(messageData => {
                  return getStoryVideoObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    username,
                    caption: messageData.caption,
                    durationSecs: messageData.media?.duration,
                    postedAtUnix: messageData.postedAtUnix,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                    isFree: !messageData.unlockPrice,
                    interactionStatistic: [
                      { type: 'ViewAction', count: messageData.views || 0 },
                      { type: 'BuyAction', count: messageData.unlocks || 0 },
                      { type: 'LikeAction', count: messageData.likes || 0 },
                      {
                        type: 'DislikeAction',
                        count: messageData.dislikes || 0,
                      },
                    ]
                      .filter(s => s.count)
                      .map(getInteractionCounter),
                  });
                }),
                ...flixDataList.map(messageData => {
                  return getFlixPreviewVideoObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    title: messageData.title,
                    description: messageData.caption || messageData.title,
                    durationSecs: 10,
                    postedAtUnix: messageData.postedAtUnix,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                  });
                }),
                ...flixDataList.map(messageData => {
                  return getFlixVideoObject({
                    id: messageData.id,
                    assetId: messageData?.assetIds?.[0],
                    title: messageData.title,
                    description: messageData.caption || messageData.title,
                    durationSecs: messageData.media?.duration || 600,
                    postedAtUnix: messageData.postedAtUnix,
                    isFree: !messageData.unlockPrice,
                    author: {
                      '@id': `https://swag.live/user/${userId}#person`,
                    },
                    interactionStatistic: [
                      { type: 'ViewAction', count: messageData.views || 0 },
                      { type: 'BuyAction', count: messageData.unlocks || 0 },
                      { type: 'LikeAction', count: messageData.likes || 0 },
                      {
                        type: 'DislikeAction',
                        count: messageData.dislikes || 0,
                      },
                    ]
                      .filter(s => s.count)
                      .map(getInteractionCounter),
                  });
                }),
              ],
            },
            { isJSON: true }
          )}
        </script>
      </Helmet>
    );
  }
}

UserProfileJsonLd.propTypes = {
  type: PropTypes.string,
  userId: PropTypes.string,
  username: PropTypes.string,
  biography: PropTypes.string,
  storyDataList: PropTypes.array,
  flixDataList: PropTypes.array,
  t: PropTypes.func.isRequired,
};

UserProfileJsonLd.defaultProps = {
  type: ProfileListType.STORY,
  userId: '',
  username: '',
  biography: '',
  storyDataList: [],
  flixDataList: [],
};

export default withTranslation()(UserProfileJsonLd);
